class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.list-menu-dropdown > .list-menu'
    };
    this.timeout;

    this.addEventListener('mouseenter', this.handleMouseover.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    // Open dropdown on focus for accessibility
    this.addEventListener('focusin', this.handleFocusIn.bind(this));
    this.addEventListener('focusout', this.handleFocusOut.bind(this));
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;

    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.resetBackground();
    this.animateMenu();
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
    document.removeEventListener('pointerdown', this.onDocumentPointerDownEvent);
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);
    this.onDocumentPointerDownEvent = this.onDocumentPointerDownEvent || this.handleDocumentPointerDown.bind(this);

    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
    document.addEventListener('pointerdown', this.onDocumentPointerDownEvent);
  }

  handleDocumentPointerDown(event) {
    if (!this.contains(event.target)) this.handleMouseleave();
  }

  addClasses() {
    const isTopLevel = this.querySelector(this.selectors.topMenuItem);

    let delay = 0;
    if (isTopLevel && !this.isHover()) {
      delay = 150;

      if (this.closest('.header--top-center') || this.closest('.header--top-left')) {
        delay = 200;
      }
    }
    this.timeout = setTimeout(() => {
      if (this.isHover()) {
        this.classList.add(this.classes.active);
      }
    }, delay);

    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);

    menus.forEach((menu) => {
      let found = false;
      const childMenus = menu.querySelectorAll('dropdown-menu');
      childMenus.forEach((element) => {
        if (element === this) {
          found = true;
          return;
        }
      });

      if (!found) menu.reset();
    });
  }

  isHover() {
    return this.header.classList.contains(this.classes.hover);
  }

  reset() {
    this.classList.remove(this.classes.active);

    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);

    clearTimeout(this.timeout);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }
  }

  close() {
    this.handleMouseleave();
  }

  handleKeydown(event) {
    const ARROW_UP = ['ArrowUp', 'Up'];
    const ARROW_DOWN = ['ArrowDown', 'Down'];
    const ARROW_LEFT = ['ArrowLeft', 'Left'];
    const ARROW_RIGHT = ['ArrowRight', 'Right'];
    const items = Array.from(this.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'));
    const activeElement = document.activeElement;
    const currentIndex = items.indexOf(activeElement);
    if (currentIndex === -1) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleMouseleave();
      this.querySelector(this.selectors.topMenuItem)?.focus();
      return;
    }

    if (ARROW_DOWN.includes(event.key)) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex].focus();
    } else if (ARROW_UP.includes(event.key)) {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + items.length) % items.length;
      items[prevIndex].focus();
    } else if (ARROW_RIGHT.includes(event.key)) {
      // Try to move right to the next column if possible
      event.preventDefault();
      const nextColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.nextElementSibling;
      if (nextColumn) {
        const nextFocusable = nextColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (nextFocusable) nextFocusable.focus();
      }
    } else if (ARROW_LEFT.includes(event.key)) {
      // Try to move left to the previous column if possible
      event.preventDefault();
      const prevColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.previousElementSibling;
      if (prevColumn) {
        const prevFocusable = prevColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (prevFocusable) prevFocusable.focus();
      }
    }
  }

  handleFocusIn(event) {
    // Only open if not already active
    if (!this.classList.contains(this.classes.active)) {
      this.bindEvent();
      this.removeClasses();
      this.addClasses();
      this.resetBackground();
      this.animateMenu();
    }
  }

  handleFocusOut() {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.handleMouseleave();
    });
  }
}
customElements.define('dropdown-menu', DropdownMenu);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo'
    };
    this.timeout = [];

    this.isClickMenu = this.classList.contains('sf-shop-mega-trigger');

    if (this.isClickMenu) {
      this.addEventListener('click', this.handleClick.bind(this));
    } else {
      this.addEventListener('mouseenter', this.handleMouseover.bind(this));
    }
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    // Open dropdown on focus for accessibility
    this.addEventListener('focusin', this.handleFocusIn.bind(this));
    this.addEventListener('focusout', this.handleFocusOut.bind(this));
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;
    
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.updateHeaderHeight();
    this.showSublist();
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(this.selectors.topMenuItem);
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();

    if (this.classList.contains(this.classes.active)) {
      this.handleMouseleave();
      return;
    }

    this.handleMouseover();
    trigger.setAttribute('aria-expanded', 'true');
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
    document.removeEventListener('pointerdown', this.onDocumentPointerDownEvent);
    document.removeEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
  }

  handleTouchMoveOutside(ev) {
    if (!this.header) return;
    
    let target_section = ev.targetTouches[0].target.closest(this.selectors.header);
    if(!target_section || this.header.classList.contains('shopify-section-header-hidden')) {
      this.reset();
      this.resetBackground();
      
      this.header.classList.remove(this.classes.hover);
      document.removeEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
    }
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);
    this.onHeaderTouchMoveOutsideEvent = this.onHeaderTouchMoveOutsideEvent || this.handleTouchMoveOutside.bind(this);
    this.onDocumentPointerDownEvent = this.onDocumentPointerDownEvent || this.handleDocumentPointerDown.bind(this);
    
    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    if (!this.isClickMenu) {
      this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
      document.addEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
    }
    document.addEventListener('pointerdown', this.onDocumentPointerDownEvent);
  }

  handleDocumentPointerDown(event) {
    if (!this.contains(event.target)) this.handleMouseleave();
  }

  showSublist() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    const background = this.header.querySelector(this.selectors.background);
    background.style.setProperty('--height', this.header.clientHeight + dropdown.clientHeight + 'px');
    background.classList.add(this.classes.active);
  }

  addClasses() {
    this.classList.add(this.classes.active);
    this.lockPageScroll();

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 150;
    childAnimate.forEach((element, index) => {
      const timeout = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      
      this.timeout.push(timeout);
    });
    
    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      menu.reset();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);
    this.unlockPageScroll();
    this.querySelector(this.selectors.topMenuItem)?.setAttribute('aria-expanded', 'false');

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((element) => {
      element.classList.remove(this.classes.animate);
    });
    this.timeout.forEach((timeout) => {
      clearTimeout(timeout);
    });
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  lockPageScroll() {
    if (!this.classList.contains('sf-shop-mega-trigger')) return;

    document.documentElement.classList.add('sf-mega-menu-open');
    document.body.classList.add('sf-mega-menu-open');
  }

  unlockPageScroll() {
    if (!this.classList.contains('sf-shop-mega-trigger')) return;

    document.documentElement.classList.remove('sf-mega-menu-open');
    document.body.classList.remove('sf-mega-menu-open');
  }

  close() {
    this.handleMouseleave();
  }

  handleKeydown(event) {
    const ARROW_UP = ['ArrowUp', 'Up'];
    const ARROW_DOWN = ['ArrowDown', 'Down'];
    const ARROW_LEFT = ['ArrowLeft', 'Left'];
    const ARROW_RIGHT = ['ArrowRight', 'Right'];
    const items = Array.from(this.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])'));
    const activeElement = document.activeElement;
    const currentIndex = items.indexOf(activeElement);
    if (currentIndex === -1) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleMouseleave();
      this.querySelector('.header__menu-item--top')?.focus();
      return;
    }

    if (ARROW_DOWN.includes(event.key)) {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex].focus();
    } else if (ARROW_UP.includes(event.key)) {
      event.preventDefault();
      const prevIndex = (currentIndex - 1 + items.length) % items.length;
      items[prevIndex].focus();
    } else if (ARROW_RIGHT.includes(event.key)) {
      // Try to move right to the next column if possible
      event.preventDefault();
      const nextColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.nextElementSibling;
      if (nextColumn) {
        const nextFocusable = nextColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (nextFocusable) nextFocusable.focus();
      }
    } else if (ARROW_LEFT.includes(event.key)) {
      // Try to move left to the previous column if possible
      event.preventDefault();
      const prevColumn = activeElement.closest('.mega-menu__item, .list-menu-dropdown')?.previousElementSibling;
      if (prevColumn) {
        const prevFocusable = prevColumn.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
        if (prevFocusable) prevFocusable.focus();
      }
    }
  }

  handleFocusIn(event) {
    if (this.isClickMenu) return;

    if (!this.classList.contains(this.classes.active)) {
      this.bindEvent();
      this.removeClasses();
      this.addClasses();
      this.updateHeaderHeight();
      this.showSublist();
    }
  }

  handleFocusOut() {
    if (this.isClickMenu) return;

    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.handleMouseleave();
    });
  }

  updateHeaderHeight() {
    const headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeaderHeight = 0;
    
    headerGroups.forEach(headerGroup => {
      totalHeaderHeight += headerGroup.offsetHeight;
    });
    
    if (totalHeaderHeight > 0) {
      document.documentElement.style.setProperty('--header-height', `${totalHeaderHeight}px`);
    }
  }

  deferredLoadCallback() {
    if (typeof this.reset === 'function') {
      this.reset();
    }
    
    requestAnimationFrame(() => {
      if (typeof this.handleMouseover === 'function') {
        this.handleMouseover();
      }
    });
  }
}
customElements.define('mega-menu', MegaMenu);

// Keep backdrop dismissal from falling through to links or product cards
// beneath the mega menu. Hold the open state through pointerup, then close on
// the resulting click while consuming that interaction.
document.addEventListener('pointerdown', (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('.sf-mega-menu-scrim')) return;

  event.preventDefault();
  event.stopPropagation();
}, true);

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || !event.target.closest('.sf-mega-menu-scrim')) return;

  event.preventDefault();
  event.stopPropagation();
  document.querySelectorAll('mega-menu.is-active').forEach((menu) => menu.close());
}, true);
